import { useEffect, useId, useRef, useState } from 'react'

import { Icon } from './Icon.tsx'
import './helpTip.css'

export type HelpTipProps = {
  /** 단추의 접근 이름 · 말풍선 이름(예: 「웹훅 주소 받는 법」) */
  label: string
  /** 말풍선 글 */
  text: string
}

/**
 * **(?) 도움말 단추 — 누르면 말풍선**(eric 2026-10-28 「물음표 아이콘 만들어서 해당 아이콘 누르면 말풍선으로 나오게」).
 *
 * 🔴 본문에 설명 문장을 깔지 않기 위한 자리다(와이어프레임 §1-1) — 「받는 법」 같은 긴 안내만 여기로 접는다.
 * 🔴 여닫기 규칙은 `DateTimePicker` 의 떠 있는 창과 같다 — 단추 `aria-expanded` · `aria-haspopup="dialog"` ·
 *    바깥 누름 · Esc 로 닫힘. 겉면도 같은 떠 있는 면 토큰(`--shadow-menu` · `--radius-md`)이다. Esc 면 포커스가 단추로 돌아온다.
 * ⚠ 비대화형 말풍선이라 포커스를 안으로 옮기지 않는다 — 글은 단추 바로 뒤에 있어 읽기 순서로 닿는다(`aria-controls`).
 * ⚠ 비활성 사유에 쓰지 마라(그쪽은 `disabledReason`) · 짧은 전문 툴팁은 `Tooltip` 이다.
 */
export function HelpTip({ label, text }: HelpTipProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLSpanElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popId = useId()

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <span className="a-helptip" ref={rootRef}>
      <button
        aria-controls={open ? popId : undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={label}
        className="a-helptip__btn"
        onClick={() => setOpen((value) => !value)}
        ref={buttonRef}
        type="button"
      >
        <Icon name="help" size="md" />
      </button>
      {open ? (
        <span aria-label={label} className="a-helptip__pop" id={popId} role="dialog">
          {text}
        </span>
      ) : null}
    </span>
  )
}
